import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { LIMITES } from '../utils/limites';
import CampoDeSenha from './CampoDeSenha';
import { SENHA_MINIMA, emailPlausivel, validarCadastro } from '../utils/erroDeAuth';
// O visual é o do cartão de login. Importado AQUI porque este componente
// também aparece no convite de meta e na autorização do conector, que não
// carregam a tela de login.
import './LoginPage.css';

/**
 * Entrar, criar conta e recuperar a senha — por email (05/10).
 *
 * ── Fechado por padrão ───────────────────────────────────────────────────
 *
 * O Google continua sendo o caminho de um toque, e é o que a tela mostra
 * primeiro. Isto aparece como um botão secundário e só abre os campos quando
 * ela pede: três campos sempre à vista dobrariam a altura do cartão para
 * servir a quem não vai usá-los.
 *
 * ── As duas telas de "enviamos um email" não dizem se a conta existe ─────
 *
 * Quando o email já tem conta, o Supabase aceita o cadastro em silêncio e não
 * manda nada (é o que impede descobrir quem usa o app). A tela não sabe qual
 * dos dois aconteceu, então fala dos DOIS: "se você não receber, é porque já
 * tem conta — entre com o Google ou peça uma senha nova". Fingir certeza aqui
 * deixaria alguém esperando um email que nunca vem.
 *
 * ── Quem sempre entrou com o Google ──────────────────────────────────────
 *
 * Cria a senha pelo "Esqueci minha senha": o link chega no email da conta e a
 * senha escolhida passa a valer para a MESMA conta. A tela diz isso, porque
 * "esqueci" não é a palavra que essa pessoa procuraria.
 */
type Passo = 'fechado' | 'entrar' | 'criar' | 'esqueci' | 'confirmacao-enviada' | 'recuperacao-enviada';

const EntradaComSenha: React.FC = () => {
  const { entrarComSenha, criarConta, pedirNovaSenha } = useAuth();
  const [passo, setPasso] = useState<Passo>('fechado');
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const ir = (p: Passo) => { setErro(null); setSenha(''); setPasso(p); };

  /* Uma porta para os três envios: trava o botão, mostra o erro ao lado do que
     falhou, e só troca de tela DEPOIS de dar certo — o que ela digitou fica. */
  const enviar = async (acao: () => Promise<string | null>, depois?: Passo) => {
    setOcupado(true);
    setErro(null);
    const falha = await acao();
    setOcupado(false);
    if (falha) return setErro(falha);
    if (depois) { setSenha(''); setPasso(depois); }
  };

  const aoEntrar = (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailPlausivel(email) || !senha) return setErro('Preencha o email e a senha.');
    void enviar(() => entrarComSenha(email, senha));
  };

  const aoCriar = (e: React.FormEvent) => {
    e.preventDefault();
    const recusa = validarCadastro({ nome, email, senha });
    if (recusa) return setErro(recusa);
    void enviar(() => criarConta({ nome, email, senha }), 'confirmacao-enviada');
  };

  const aoEsquecer = (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailPlausivel(email)) return setErro('Esse email não parece válido. Confira e tente de novo.');
    void enviar(() => pedirNovaSenha(email), 'recuperacao-enviada');
  };

  if (passo === 'fechado') {
    return (
      <button type="button" className="login-senha-abrir" onClick={() => ir('entrar')}>
        Entrar com email e senha
      </button>
    );
  }

  if (passo === 'confirmacao-enviada') {
    return (
      <div className="login-aviso" role="status">
        <p className="login-aviso-titulo">Confira seu email</p>
        <p>
          Enviamos um link para <strong>{email.trim()}</strong>. Abra para confirmar e entrar.
        </p>
        <p className="login-aviso-nota">
          Não chegou em alguns minutos? Olhe o spam. Se esse email já tem conta, não enviamos nada:
          entre com o Google ou use "Esqueci minha senha".
        </p>
        <button type="button" className="login-link" onClick={() => ir('entrar')}>Voltar para entrar</button>
      </div>
    );
  }

  if (passo === 'recuperacao-enviada') {
    return (
      <div className="login-aviso" role="status">
        <p className="login-aviso-titulo">Confira seu email</p>
        <p>
          Se <strong>{email.trim()}</strong> tem conta no FinTracking, enviamos um link para escolher
          uma senha nova.
        </p>
        <p className="login-aviso-nota">Não chegou em alguns minutos? Olhe o spam.</p>
        <button type="button" className="login-link" onClick={() => ir('entrar')}>Voltar para entrar</button>
      </div>
    );
  }

  const campoDeEmail = (
    <label className="login-campo">
      <span>Email</span>
      <input
        type="email" inputMode="email" autoComplete="email" autoCapitalize="none" spellCheck={false}
        value={email} onChange={e => setEmail(e.target.value)} disabled={ocupado} required
      />
    </label>
  );

  if (passo === 'esqueci') {
    return (
      <form className="login-form" onSubmit={aoEsquecer} noValidate>
        <p className="login-form-intro">
          Enviamos um link para você escolher uma senha nova. Serve também para criar uma senha se
          você sempre entrou com o Google.
        </p>
        {campoDeEmail}
        {erro && <p className="login-erro" role="alert">{erro}</p>}
        <button className="login-enviar" disabled={ocupado} aria-busy={ocupado}>Enviar link</button>
        <div className="login-links">
          <button type="button" className="login-link" onClick={() => ir('entrar')}>Voltar para entrar</button>
        </div>
      </form>
    );
  }

  if (passo === 'criar') {
    return (
      <form className="login-form" onSubmit={aoCriar} noValidate>
        <label className="login-campo">
          <span>Como podemos te chamar</span>
          <input
            type="text" autoComplete="name" maxLength={LIMITES.nome}
            value={nome} onChange={e => setNome(e.target.value)} disabled={ocupado} required
          />
        </label>
        {campoDeEmail}
        <CampoDeSenha
          rotulo="Senha" valor={senha} aoMudar={setSenha} autoComplete="new-password"
          desabilitado={ocupado} minLength={SENHA_MINIMA}
          dica={`Pelo menos ${SENHA_MINIMA} caracteres.`}
        />
        {erro && <p className="login-erro" role="alert">{erro}</p>}
        <button className="login-enviar" disabled={ocupado} aria-busy={ocupado}>Criar conta</button>
        <div className="login-links">
          <button type="button" className="login-link" onClick={() => ir('entrar')}>Já tenho conta</button>
        </div>
      </form>
    );
  }

  return (
    <form className="login-form" onSubmit={aoEntrar} noValidate>
      {campoDeEmail}
      <CampoDeSenha
        rotulo="Senha" valor={senha} aoMudar={setSenha} autoComplete="current-password"
        desabilitado={ocupado}
      />
      {erro && <p className="login-erro" role="alert">{erro}</p>}
      <button className="login-enviar" disabled={ocupado} aria-busy={ocupado}>Entrar</button>
      <div className="login-links">
        <button type="button" className="login-link" onClick={() => ir('esqueci')}>Esqueci minha senha</button>
        <button type="button" className="login-link" onClick={() => ir('criar')}>Criar conta</button>
      </div>
    </form>
  );
};

export default EntradaComSenha;
